import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import './App.css';

const IMAGE_BASE = 'https://mitchelsneddon.s3.eu-west-1.amazonaws.com';
const resolveImageSrc = (imagePath) =>
  imagePath?.startsWith('http') ? imagePath : `${IMAGE_BASE}${imagePath}`;

const portfolioItems = [
  {
    id: 8,
    slug: 'aqqut',
    image: 'https://mitchelsneddon.s3.eu-west-1.amazonaws.com/aqqut.png',
    title: 'AQQUT',
    eyebrow: 'Building intelligence for the North',
    role: 'Full-stack contributor',
    tagline: 'React and C# building operations for extreme, low-connectivity environments.',
    result: 'Secure monitoring, predictive maintenance, and AI-assisted operator workflows.',
    cardTone: '#176847',
    featured: true,
    overview:
      'AQQUT is an Arctic-ready building automation and predictive maintenance platform for residential, commercial, and government facilities across the North. I contributed across the React and C# product, collaborating with the client and delivery team to turn complex operational needs into practical tools.',
    challenge:
      'Operators need clear, trustworthy signals from building systems in extreme conditions, including locations where connectivity can be limited and equipment failure carries a high cost.',
    contribution:
      'I helped deliver secure real-time monitoring experiences and AI assistance grounded in sensor manuals through retrieval-augmented generation. The work required balancing technical capability with understandable actions for operators.',
    outcome:
      'The platform brings monitoring, freeze and water protection, fuel and energy insight, and predictive maintenance into one operator-focused experience.',
    skills: ['React', 'C# / .NET', 'Azure', 'RAG', 'Real-time data']
  },
  {
    id: 2,
    slug: 'check-the-label',
    image: '/checkthelabel.PNG',
    title: 'Check The Label',
    eyebrow: 'Canadian products, made simple',
    role: 'React Native delivery lead',
    tagline: 'A mobile product that helps Canadians identify and choose Canadian-made goods.',
    result: 'Reached the top 15 on both Canadian iOS and Android app stores.',
    cardTone: '#e85e45',
    featured: true,
    overview:
      'Check The Label is a web and mobile product that helps Canadians quickly identify, select, and purchase Canadian-made products. Users can scan a barcode or capture a photo to identify product details.',
    challenge:
      'The product needed to make a large and changing product catalogue useful in a fast, camera-first mobile experience while supporting releases across two app stores.',
    contribution:
      'I led React Native delivery, coordinating product direction, application architecture, store releases, and production support across iOS and Android.',
    outcome:
      'The application reached the top 15 on both Canadian app stores for several weeks and received national media coverage.',
    link: 'https://www.checkthelabel.ca/',
    skills: ['React Native', 'APIs', 'AI features', 'iOS', 'Android']
  },
  {
    id: 1,
    slug: 'becky',
    image: '/becky.PNG',
    title: 'Becky',
    eyebrow: 'Admissions support, available all year',
    role: 'Product and technical lead',
    tagline: 'An early university admissions chatbot built around a measurable student need.',
    result: 'Increased applicant conversion by 25% and contributed to $30M+ in tuition revenue.',
    cardTone: '#3579e8',
    featured: true,
    overview:
      'Becky began with an unmet need: prospective students needed useful admissions support outside office hours. I proposed the product and led it from early experimentation through production launch.',
    challenge:
      'Admissions questions are time-sensitive and personal. The experience needed to be useful at any hour while connecting prospective students to accurate information and the next step in their application.',
    contribution:
      'I combined product discovery, conversational experience design, technical delivery, integrations, and stakeholder collaboration to build and launch the service.',
    outcome:
      'Becky increased applicant conversion by 25%, contributed more than $30M in tuition revenue, and helped earn multiple digital innovation awards.',
    link: 'https://www.bbc.com/news/technology-40960426',
    skills: ['Conversational AI', 'Product discovery', 'Integrations', 'Delivery leadership']
  },
  {
    id: 3,
    slug: 'leeds-beckett-redesign',
    image: '/leedsbeckett.PNG',
    title: 'Leeds Beckett Redesign',
    eyebrow: 'University web platform',
    role: 'Full-stack developer',
    tagline: 'A modern, maintainable web experience for prospective and current students.',
    result: 'Introduced React architecture and reusable patterns across multiple applications.',
    cardTone: '#7443bd',
    overview:
      'I helped lead modernization of the university website and mobile platform around React, improving performance, maintainability, and the team\'s ability to deliver new experiences.',
    challenge:
      'A high-traffic university platform needs to serve many audiences while remaining manageable for both developers and content teams.',
    contribution:
      'I defined frontend architecture, reusable component structures, and development standards while delivering .NET services, SQL Server solutions, integrations, and secure identity flows.',
    outcome:
      'The modernization created a stronger foundation for new recruitment and student experiences across the university.',
    link: 'https://www.leedsbeckett.ac.uk/',
    skills: ['React', 'C# / .NET', 'Sitecore', 'SQL Server', 'Identity']
  },
  {
    id: 4,
    slug: 'punchygpt',
    image: '/punchy.PNG',
    title: 'PunchyGPT',
    eyebrow: 'Knowledge where the team works',
    role: 'Full-stack developer',
    tagline: 'AI assistance delivered directly through Microsoft Teams.',
    result: 'Connected OpenAI-powered support to familiar internal workflows.',
    cardTone: '#e96b4e',
    overview:
      'PunchyGPT is a Microsoft Teams assistant that makes AI support available where staff already communicate and collaborate.',
    challenge:
      'Useful internal AI needs to fit existing work patterns rather than creating another destination for people to remember.',
    contribution:
      'I designed and launched the Teams experience, integrating OpenAI-powered responses with practical enterprise workflows.',
    outcome:
      'The assistant reduced friction around accessing AI support and provided a foundation for deeper knowledge and workflow integrations.',
    link: 'https://github.com/mitchelsneddon/mitchelGPT',
    skills: ['TypeScript', 'Microsoft Teams', 'OpenAI', 'Bot development']
  },
  {
    id: 9,
    slug: 'every-movie-is-a-workout',
    image: 'https://mitchelsneddon.s3.eu-west-1.amazonaws.com/wattch.png',
    title: 'Every Movie Is A Workout',
    eyebrow: 'Connected fitness experiment',
    role: 'Mobile developer',
    tagline: 'Story-led resistance training synchronized with films and television.',
    result: 'Connected mobile storytelling with smart-trainer hardware and Strava.',
    cardTone: '#232323',
    overview:
      'A React Native mobile application that turns films or television episodes into dynamic, story-led workouts using a connected bike trainer.',
    challenge:
      'The experience had to translate narrative moments into physical resistance changes while communicating reliably with external fitness hardware.',
    contribution:
      'I helped build the connected mobile experience, including trainer communication and Strava integration, and collaborated with Wahoo and KICKR.',
    outcome:
      'The concept demonstrated a distinctive way to combine entertainment, mobile software, and connected fitness hardware.',
    skills: ['React Native', 'Connected devices', 'Strava', 'Wahoo', 'Mobile']
  },
  {
    id: 5,
    slug: 'course-takeaway',
    image: '/coursetakeaway.PNG',
    title: 'Course Takeaway',
    eyebrow: 'Experiential course discovery',
    role: 'Product and full-stack developer',
    tagline: 'A large-format course selector inspired by fast-food ordering.',
    result: 'Won multiple awards and introduced a new approach to recruitment events.',
    cardTone: '#ef8e91',
    overview:
      'Course Takeaway transformed a large touchscreen and receipt printer into an approachable university course selector.',
    challenge:
      'Traditional course-search tools were not designed to attract attention or create a memorable interaction at busy recruitment events.',
    contribution:
      'I helped shape the concept and delivered the interactive experience across frontend, backend, CMS, and physical event hardware.',
    outcome:
      'The concept won multiple awards and helped establish new digital marketing approaches for the university.',
    skills: ['React', 'C# / .NET', 'Sitecore', 'UX', 'Hardware integration']
  },
  {
    id: 6,
    slug: 'ucas-calculator',
    image: '/ucascalc.PNG',
    title: 'UCAS Calculator',
    eyebrow: 'Clearer course decisions',
    role: 'Full-stack developer',
    tagline: 'A practical tool for converting results into course options.',
    result: 'Made a complex admissions calculation immediate and understandable.',
    cardTone: '#f4ce75',
    overview:
      'The UCAS Calculator helps prospective students convert school results into UCAS points and connect those results to relevant course options.',
    challenge:
      'Qualification rules can make it difficult for students to understand their options quickly and confidently.',
    contribution:
      'I built the interactive calculation and course-discovery experience with a focus on clarity and immediate feedback.',
    outcome:
      'The tool turned a complicated admissions task into a simple, useful step in course discovery.',
    skills: ['React', 'C# / .NET', 'APIs', 'UX']
  },
  {
    id: 10,
    slug: 'fleet-management',
    image: 'https://mitchelsneddon.s3.eu-west-1.amazonaws.com/alata.png',
    title: 'Fleet Management',
    eyebrow: 'Real-time operations',
    role: 'Full-stack developer',
    tagline: 'One dashboard for vehicles, maintenance, costs, and compliance.',
    result: 'Turned factory-installed vehicle data into clearer operational decisions.',
    cardTone: '#3b869a',
    overview:
      'A React and C# web application that gives fleet operators an understandable view of vehicles, maintenance, costs, and daily office workflows.',
    challenge:
      'Useful fleet decisions depend on combining live vehicle signals with maintenance, compliance, driver, and cost workflows.',
    contribution:
      'I helped build the full-stack product around existing factory-installed vehicle technology, including dashboard and workflow experiences.',
    outcome:
      'The platform gave operators a real-time view across GPS, driver behaviour, fuel, inspections, maintenance, compliance, and expenses.',
    skills: ['React', 'C# / .NET', 'GPS data', 'APIs', 'Operational UX']
  }
];

const capabilities = [
  { number: '01', title: 'Product discovery', copy: 'Turn incomplete requirements into testable plans, surface trade-offs early, and keep the work tied to a real user or business outcome.' },
  { number: '02', title: 'Full-stack delivery', copy: 'Move comfortably between React, TypeScript, C#/.NET, Node.js, React Native, SQL, integrations, and cloud delivery.' },
  { number: '03', title: 'Production ownership', copy: 'Design for operability, ship through CI/CD, support releases, diagnose incidents, and improve the product after launch.' },
  { number: '04', title: 'Technical leadership', copy: 'Set reusable patterns, explain decisions, collaborate across product and design, and help other developers do their best work.' }
];

const cardReveal = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } }
};

function WorkCard({ item, onOpen, featured = false, reduceMotion = false }) {
  return (
    <motion.a
      href={`#project-${item.slug}`}
      className={`work-card ${featured ? 'work-card-featured' : ''}`}
      style={{ '--card-tone': item.cardTone }}
      onClick={(event) => onOpen(item, event)}
      variants={reduceMotion ? undefined : cardReveal}
      whileHover={reduceMotion ? undefined : { y: -5 }}
      whileTap={reduceMotion ? undefined : { scale: 0.99 }}
    >
      <div className="work-image-wrap">
        <img src={resolveImageSrc(item.image)} alt="" className="work-image" loading="lazy" />
      </div>
      <div className="work-copy">
        <p className="work-eyebrow">{item.eyebrow}</p>
        <h3>{item.title}</h3>
        <p className="work-role">{item.role}</p>
        <p className="work-result">{item.result}</p>
        <div className="card-skills" aria-label={`${item.title} technologies`}>
          {item.skills.slice(0, featured ? 5 : 3).map((skill) => (
            <span key={`${item.id}-${skill}`}>{skill}</span>
          ))}
        </div>
        <span className="work-link">Read case study <span aria-hidden="true">↗</span></span>
      </div>
    </motion.a>
  );
}

function App() {
  const [selectedItem, setSelectedItem] = useState(null);
  const modalRef = useRef(null);
  const pageRef = useRef(null);
  const triggerRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const featuredItems = useMemo(() => portfolioItems.filter((item) => item.featured), []);
  const remainingItems = useMemo(() => portfolioItems.filter((item) => !item.featured), []);

  const openCaseStudy = (item, event) => {
    event?.preventDefault();
    triggerRef.current = event?.currentTarget || null;
    window.history.pushState({ project: item.slug }, '', `#project-${item.slug}`);
    setSelectedItem(item);
  };

  const closeCaseStudy = () => {
    window.history.pushState({}, '', `${window.location.pathname}${window.location.search}`);
    setSelectedItem(null);
  };

  useEffect(() => {
    const syncProjectFromUrl = () => {
      const slug = window.location.hash.replace('#project-', '');
      setSelectedItem(portfolioItems.find((item) => item.slug === slug) || null);
    };
    syncProjectFromUrl();
    window.addEventListener('popstate', syncProjectFromUrl);
    return () => window.removeEventListener('popstate', syncProjectFromUrl);
  }, []);

  useEffect(() => {
    const pageNode = pageRef.current;
    if (!selectedItem) {
      pageNode?.removeAttribute('inert');
      pageNode?.removeAttribute('aria-hidden');
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    pageNode?.setAttribute('inert', '');
    pageNode?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = 'hidden';
    const focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeCaseStudy();
        return;
      }
      if (event.key !== 'Tab' || !modalRef.current) return;
      const focusable = Array.from(modalRef.current.querySelectorAll(focusableSelector));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.requestAnimationFrame(() => modalRef.current?.querySelector('.modal-close')?.focus());
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      pageNode?.removeAttribute('inert');
      pageNode?.removeAttribute('aria-hidden');
      window.requestAnimationFrame(() => triggerRef.current?.focus());
    };
  }, [selectedItem]);

  return (
    <div className="app-shell">
      <div ref={pageRef} className="page-shell">
        <a className="skip-link" href="#main-content">Skip to content</a>
        <div className="splotch splotch-a" aria-hidden="true" />
        <div className="splotch splotch-b" aria-hidden="true" />
        <div className="splotch splotch-c" aria-hidden="true" />
        <div className="splotch splotch-d" aria-hidden="true" />
        <div className="splotch splotch-e" aria-hidden="true" />

        <header className="site-header">
          <a className="brand" href="#top" aria-label="Mitchel Sneddon, home">Mitchel Sneddon</a>
          <nav aria-label="Primary navigation">
            <a href="#work">Work</a>
            <a href="#experience">Experience</a>
            <a href="#about">About</a>
            <a className="nav-cta" href="#contact">Get in touch</a>
          </nav>
        </header>

        <main id="main-content">
          <motion.section className="hero content-width" id="top" initial={reduceMotion ? false : { opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reduceMotion ? 0 : 0.6 }}>
            <h1>Full-stack developer turning ambiguous problems into useful, production-ready products.</h1>
            <p className="hero-lead">I have 9+ years of experience taking web, mobile, backend, and AI-enabled products from early discovery through launch and production support. I work comfortably through ambiguity, pairing pragmatic engineering with strong product thinking to build secure, scalable experiences that deliver measurable results.</p>
          </motion.section>

          <section className="work-section content-width" id="work">
            <div className="section-heading section-heading-split">
              <div><p className="section-kicker">Selected work</p><h2>Products with a purpose—and proof.</h2></div>
              <p>Three projects that show how I connect product thinking, engineering decisions, and measurable outcomes.</p>
            </div>
            <motion.div className="featured-grid" initial={reduceMotion ? false : 'hidden'} whileInView="visible" viewport={{ once: true, amount: 0.08 }} transition={{ staggerChildren: reduceMotion ? 0 : 0.1 }}>
              {featuredItems.map((item) => <WorkCard key={item.id} item={item} onOpen={openCaseStudy} featured reduceMotion={reduceMotion} />)}
            </motion.div>
          </section>

          <section className="experience-section" id="experience">
            <div className="content-width experience-layout">
              <div className="section-heading experience-intro"><p className="section-kicker">Experience</p><h2>From unclear brief to supported product.</h2><p>I work across the full delivery lifecycle while keeping technical choices connected to the outcome.</p></div>
              <div className="timeline">
                <article className="timeline-item"><div className="timeline-meta"><p>2022—Present</p><p>Vancouver, BC</p></div><div><h3>Punchcard Systems</h3><p className="timeline-role">Full-Stack Developer</p><p>Own client delivery from discovery and technical direction through implementation, release, and production support. Build with React, TypeScript, C#/.NET, Node.js, React Native, Azure, Docker, and Azure DevOps.</p></div></article>
                <article className="timeline-item"><div className="timeline-meta"><p>2016—2022</p><p>Leeds, UK</p></div><div><h3>Leeds Beckett University</h3><p className="timeline-role">Full-Stack Developer</p><p>Modernized web and mobile platforms, defined reusable frontend patterns, built .NET and SQL services, and created award-winning recruitment products with measurable conversion impact.</p></div></article>
              </div>
            </div>
          </section>

          <section className="capabilities-section content-width" id="about">
            <div className="section-heading section-heading-split"><div><p className="section-kicker">How I work</p><h2>Broad enough to connect the system. Practical enough to ship it.</h2></div><p>React and C#/.NET are my deepest stack. My broader experience helps me see the decisions between product, interface, backend, cloud, and delivery.</p></div>
            <div className="capabilities-grid">
              {capabilities.map((capability) => <article key={capability.number} className="capability-card"><span>{capability.number}</span><h3>{capability.title}</h3><p>{capability.copy}</p></article>)}
            </div>
          </section>

          <section className="more-work-section content-width" id="more-work">
            <div className="section-heading section-heading-split"><div><p className="section-kicker">More work</p><h2>Platforms, experiments, and practical tools.</h2></div><p>Additional work across web platforms, AI, connected fitness, recruitment, and real-time operations.</p></div>
            <div className="projects-grid">{remainingItems.map((item) => <WorkCard key={item.id} item={item} onOpen={openCaseStudy} reduceMotion={reduceMotion} />)}</div>
          </section>

          <section className="contact-section" id="contact">
            <div className="content-width contact-layout"><p className="section-kicker">What’s next</p><h2>Looking for a full-stack developer who can connect product and engineering?</h2><p>I’m based in Vancouver and open to full-stack developer roles where I can own meaningful work from discovery through production.</p><div className="hero-actions"><a className="button button-primary" href="mailto:mitchelsneddon@gmail.com">Start a conversation</a><a className="button button-secondary" href="https://drive.google.com/file/d/1A-692V0G_sMAOLjclR8WEZJxNdMpf0cd/view?usp=sharing" target="_blank" rel="noreferrer">View CV</a><a className="text-link" href="https://www.linkedin.com/in/mitchelsneddon" target="_blank" rel="noreferrer">LinkedIn <span aria-hidden="true">↗</span></a></div></div>
          </section>
        </main>

      </div>

      {selectedItem ? (
        <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && closeCaseStudy()}>
          <motion.article className="modal-card" ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="case-study-title" initial={reduceMotion ? false : { opacity: 0, scale: 0.98, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: reduceMotion ? 0 : 0.2 }}>
            <button type="button" className="modal-close" aria-label="Close case study" onClick={closeCaseStudy}>×</button>
            <div className="modal-hero" style={{ '--modal-tone': selectedItem.cardTone }}><img src={resolveImageSrc(selectedItem.image)} alt="" /></div>
            <div className="modal-content">
              <p className="section-kicker">{selectedItem.eyebrow}</p><h2 id="case-study-title">{selectedItem.title}</h2><p className="modal-result">{selectedItem.result}</p>
              <div className="case-meta"><div><span>Role</span><strong>{selectedItem.role}</strong></div><div><span>Stack</span><strong>{selectedItem.skills.join(' · ')}</strong></div></div>
              <div className="case-study-grid"><section><h3>Overview</h3><p>{selectedItem.overview}</p></section><section><h3>The challenge</h3><p>{selectedItem.challenge}</p></section><section><h3>My contribution</h3><p>{selectedItem.contribution}</p></section><section><h3>Outcome</h3><p>{selectedItem.outcome}</p></section></div>
              <div className="modal-actions">{selectedItem.link ? <a className="button button-primary" href={selectedItem.link} target="_blank" rel="noreferrer">View supporting link <span aria-hidden="true">↗</span></a> : <p className="client-note">Client work · Additional detail available in an interview.</p>}<button type="button" className="button button-secondary" onClick={closeCaseStudy}>Back to work</button></div>
            </div>
          </motion.article>
        </div>
      ) : null}
    </div>
  );
}

export default App;
